import React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { cn } from '@/design-system/lib/utils';
import { ProgressBar } from '@/design-system/progress-bar';
import { Tag } from '@/design-system/tag';

import {
  metaOf,
  SOURCE_META,
  STOCK_TYPE_META,
  type StatusMeta,
} from '../status';
import type { OutboundOrderSource, WarehouseStockType } from '../types';
import { WAREHOUSE_PATHS } from '../paths';

/* ------------------------------ text tokens ------------------------------ */

export const codeCls =
  'font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums';
export const nameCls = 'text-[13px] text-text-strong-950 tracking-tight';
export const metaCls = 'text-[11px] text-text-sub-600';
export const numCls = 'font-mono text-[12px] text-text-strong-950 tabular-nums';
export const dateCls = 'text-[12px] tabular-nums text-text-sub-600';
export const linkCls =
  'font-mono text-[12px] font-semibold tabular-nums text-accent hover:underline underline-offset-4';

export const Dash: React.FC = () => (
  <span className="text-text-soft-400">—</span>
);

/* -------------------------------- headers -------------------------------- */

interface PageHeaderProps {
  eyebrow: string;
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}

/** Page shell header (CLAUDE.md § Page shell) — eyebrow, title, lede, actions. */
export const PageHeader: React.FC<PageHeaderProps> = ({
  eyebrow,
  title,
  description,
  actions,
}) => (
  <header className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
    <div className="min-w-0 flex-1">
      <p className="label-eyebrow mb-2">
        <span className="text-accent font-bold mr-3">06</span>
        {eyebrow}
      </p>
      <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
        {title}
      </h1>
      {description && (
        <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
          {description}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:flex-nowrap">
        {actions}
      </div>
    )}
  </header>
);

interface DetailHeaderProps {
  backTo: string;
  backLabel: string;
  meta?: React.ReactNode;
  title: React.ReactNode;
  badges?: React.ReactNode;
  actions?: React.ReactNode;
}

/** Detail page header — back link, mono document code, badges, actions. */
export const DetailHeader: React.FC<DetailHeaderProps> = ({
  backTo,
  backLabel,
  meta,
  title,
  badges,
  actions,
}) => (
  <header className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] text-text-sub-600 transition-colors hover:text-accent"
      >
        <i className="ri-arrow-left-line text-[14px]" aria-hidden />
        {backLabel}
      </Link>
      {meta && (
        <span className="truncate text-[11px] text-text-soft-400">{meta}</span>
      )}
    </div>
    <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
        <h1 className="font-mono text-[22px] font-extrabold tracking-tight text-text-strong-950 leading-none">
          {title}
        </h1>
        {badges}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  </header>
);

/* ------------------------------- surfaces -------------------------------- */

interface SectionProps {
  title: string;
  description?: React.ReactNode;
  right?: React.ReactNode;
  /** Drop the body padding (tables that carry their own frame). */
  flush?: boolean;
  className?: string;
  children: React.ReactNode;
}

/** Card with a titled header row — the building block of detail pages. */
export const Section: React.FC<SectionProps> = ({
  title,
  description,
  right,
  flush,
  className,
  children,
}) => (
  <Card padding="none" className={className}>
    <div className="flex items-center justify-between gap-3 border-b border-stroke-soft-200 px-4 py-3">
      <div className="min-w-0">
        <h3 className="text-[15px] font-bold tracking-tight text-text-strong-950">
          {title}
        </h3>
        {description && <p className={cn('mt-0.5', metaCls)}>{description}</p>}
      </div>
      {right}
    </div>
    <div className={flush ? '' : 'px-4 py-3'}>{children}</div>
  </Card>
);

const isBlank = (v: React.ReactNode) =>
  v === null || v === undefined || v === '' || v === false;

/** Label / value row (ShipmentDetailPanel › Details style). */
export const KV: React.FC<{
  label: string;
  value?: React.ReactNode;
  sub?: React.ReactNode;
}> = ({ label, value, sub }) => (
  <div className="flex items-start justify-between gap-4 border-b border-stroke-soft-200 py-2.5 last:border-0">
    <span className="text-[13px] text-text-sub-600">{label}</span>
    <span className="text-right text-[13px] font-medium text-text-strong-950">
      {isBlank(value) ? <Dash /> : value}
      {sub && (
        <span className="mt-0.5 block text-[11px] font-normal text-text-sub-600">
          {sub}
        </span>
      )}
    </span>
  </div>
);

/** Two-column definition grid with eyebrow labels. */
export const KVGrid: React.FC<{ items: [string, React.ReactNode][] }> = ({
  items,
}) => (
  <dl className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
    {items.map(([label, value]) => (
      <div key={label} className="border-b border-stroke-soft-200 py-2.5">
        <dt className="label-eyebrow">{label}</dt>
        <dd className="mt-1 text-[13px] font-medium text-text-strong-950">
          {isBlank(value) ? <Dash /> : value}
        </dd>
      </div>
    ))}
  </dl>
);

const SUM_TONE = {
  success: 'bg-(--state-success-lighter) text-(--state-success-dark)',
  information:
    'bg-(--state-information-lighter) text-(--state-information-dark)',
  error: 'bg-(--state-error-lighter) text-(--state-error-dark)',
  warning: 'bg-(--state-warning-lighter) text-(--state-warning-dark)',
  neutral: 'bg-bg-weak-50 text-text-strong-950',
} as const;

/** Tinted summary tile (CSV preview). */
export const SumTile: React.FC<{
  label: string;
  value: number;
  tone: keyof typeof SUM_TONE;
  testId?: string;
}> = ({ label, value, tone, testId }) => (
  <div
    className={cn('rounded-xl px-4 py-3', SUM_TONE[tone])}
    data-testid={testId}
  >
    <p className="text-[10px] font-bold uppercase tracking-[0.18em]">{label}</p>
    <p className="mt-1.5 text-[24px] font-extrabold leading-none tracking-tight tabular-nums">
      {value}
    </p>
  </div>
);

/* -------------------------------- badges --------------------------------- */

export const StatusBadge: React.FC<{
  meta: StatusMeta;
  className?: string;
}> = ({ meta, className }) => (
  <Badge status={meta.tone} type="dot" size="sm" className={className}>
    {meta.label}
  </Badge>
);

/** Badge from a status map + key, unknown keys render as neutral text. */
export const MappedBadge: React.FC<{
  map: Record<string, StatusMeta>;
  value: string | null | undefined;
}> = ({ map, value }) => <StatusBadge meta={metaOf(map, value)} />;

/** REGULAR / RETURN of a stock row; nothing for a row that has no type. */
export const StockTypeBadge: React.FC<{
  type: WarehouseStockType | null | undefined;
}> = ({ type }) =>
  type ? <MappedBadge map={STOCK_TYPE_META} value={type} /> : null;

export const SourceTag: React.FC<{ source: OutboundOrderSource }> = ({
  source,
}) => {
  const meta = SOURCE_META[source] ?? SOURCE_META.MANUAL;
  return (
    <Tag
      size="xs"
      variant={source === 'CSV' ? 'stroke' : 'gray'}
      icon={<i className={cn(meta.icon, 'text-[11px]')} />}
    >
      {meta.tag}
    </Tag>
  );
};

/* -------------------------------- timeline ------------------------------- */

export interface TimelineEntry {
  id: string;
  at: string;
  title: string;
  note?: string;
  by?: string;
  badge?: React.ReactNode;
  icon?: string;
}

/**
 * Activity log with a rail (the Shipments timeline look): a dot with the
 * event's icon, a connector down to the next event, title + badge, the
 * time on the right, the actor and note under it.
 */
export const Timeline: React.FC<{
  entries: TimelineEntry[];
  formatAt: (iso: string) => string;
}> = ({ entries, formatAt }) => {
  if (!entries.length) return <p className={metaCls}>Belum ada aktivitas.</p>;
  return (
    <ol>
      {entries.map((e, i) => {
        const last = i === entries.length - 1;
        return (
          <li key={e.id} className="flex gap-3">
            <div className="flex flex-col items-center self-stretch">
              <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-stroke-soft-200 bg-bg-white-0">
                <i
                  className={cn(
                    e.icon || 'ri-time-line',
                    'text-[10px] text-text-sub-600'
                  )}
                  aria-hidden
                />
              </span>
              {!last && <span className="my-1 w-0.5 flex-1 bg-stroke-soft-200" />}
            </div>
            <div className={cn('min-w-0 flex-1', !last && 'pb-4')}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {e.badge}
                  <span className="text-[12px] font-semibold text-text-strong-950">
                    {e.title}
                  </span>
                </div>
                <span className="shrink-0 text-[11px] tabular-nums text-text-sub-600">
                  {formatAt(e.at)}
                </span>
              </div>
              {e.by && (
                <p className="mt-0.5 text-[11px] text-text-sub-600">{e.by}</p>
              )}
              {e.note && (
                <p className="mt-1 text-[11px] leading-snug text-text-sub-600">
                  {e.note}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

/* --------------------------------- states -------------------------------- */

/** Card-framed kit EmptyState — hub required, load error, … */
export const StateBox: React.FC<{
  icon: string;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <Card padding="none">
    <EmptyState size="md" className="py-12">
      <EmptyStateIcon className="text-icon-soft-400">
        <i className={cn(icon, 'text-[20px]')} aria-hidden />
      </EmptyStateIcon>
      <EmptyStateTitle>{title}</EmptyStateTitle>
      {description && (
        <EmptyStateDescription className="max-w-md">
          {description}
        </EmptyStateDescription>
      )}
      {action && (
        <EmptyStateActions className="mt-0">{action}</EmptyStateActions>
      )}
    </EmptyState>
  </Card>
);

export const HubRequired: React.FC = () => (
  <StateBox icon="ri-building-2-line" title="Pilih hub dulu" />
);

export const LoadError: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <StateBox
    icon="ri-error-warning-line"
    title="Gagal memuat data"
    description={message}
    action={
      <Button tone="neutral" style="stroke" size="sm" onClick={onRetry}>
        Coba lagi
      </Button>
    }
  />
);

export const DetailSkeleton: React.FC = () => (
  <div className="space-y-5 p-6" aria-busy="true">
    <div className="h-16 animate-pulse rounded-2xl bg-bg-weak-50" />
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
      <div className="h-64 animate-pulse rounded-2xl bg-bg-weak-50 xl:col-span-2" />
      <div className="h-64 animate-pulse rounded-2xl bg-bg-weak-50" />
    </div>
  </div>
);

/**
 * "Request / External ID" cell of the goods-issue lists: order code linking
 * to the outbound request + client ref; falls back to the GI `reference`
 * for standalone v1 goods issues.
 */
export const OrderRefCell: React.FC<{
  order: { id: string; code: string; externalID: string } | null;
  reference: string;
}> = ({ order, reference }) =>
  order ? (
    <div>
      <Link
        to={`${WAREHOUSE_PATHS.outboundRequest}/${order.id}`}
        onClick={(e) => e.stopPropagation()}
        className={linkCls}
      >
        {order.code}
      </Link>
      <p className={`${metaCls} font-mono`}>{order.externalID}</p>
    </div>
  ) : (
    <span className={codeCls}>{reference || '—'}</span>
  );

/** Small "Lines / qty" cell. */
export const LinesQty: React.FC<{ lines: number; qty: string }> = ({
  lines,
  qty,
}) => (
  <span className="text-[13px] tabular-nums text-text-sub-600">
    {lines} / {qty}
  </span>
);

/** Received / expected bar (inbound progress) — kit ProgressBar, role=progressbar. */
export const ProgressCell: React.FC<{
  received: number;
  expected: number;
  format: (n: number) => string;
}> = ({ received, expected, format }) => {
  const pct = expected
    ? Math.min(100, Math.round((received / expected) * 100))
    : 0;
  const text = `${format(received)} / ${format(expected)}`;
  return (
    <div className="flex min-w-[120px] flex-col gap-1">
      <span className="font-mono text-[12px] tabular-nums text-text-strong-950">
        {text}
      </span>
      <ProgressBar
        size="sm"
        value={pct}
        aria-label="Diterima"
        aria-valuenow={pct}
        aria-valuetext={`${text} (${pct}%)`}
      />
    </div>
  );
};
